import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, GlobalStyles, Paper, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, GridApi } from "ag-grid-community";
import { ErrorModal } from "@dexteel/mesf-core";
import moment from "moment";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { useReportPdfExport } from "@/shared/hooks/useReportPdfExport";
import {
	isUsableDate,
	urlParam,
	useUrlState,
} from "@/shared/hooks/useUrlState";
import { getReportPdf } from "@/shared/repositories/ReportPdfRepository";
import { gridToPdfTable } from "@/shared/utils/gridToPdfTable";
import {
	useCostCenters,
	useFirstTurnedRolls,
} from "../hooks/useFirstTurnedRolls";
import type { FirstTurnedRollRow } from "../models/FirstTurnedRolls";
import { FirstTurnedRollsSummary } from "./FirstTurnedRollsSummary";
import { FirstTurnedRollsToolbar } from "./FirstTurnedRollsToolbar";

const CENTER_CELL = { textAlign: "center" } as const;
const CENTER_HEADER = "ag-cell-center-header";

const toFixedOrEmpty = (value: unknown, decimals: number): string =>
	value == null ? "" : Number(value).toFixed(decimals);

const columnDefs: ColDef<FirstTurnedRollRow>[] = [
	{
		field: "MillSize",
		headerName: "Mill Size",
		width: 110,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "FirstTurnTimestampString",
		headerName: "Date First Turned",
		width: 150,
		sortable: true,
		sort: "asc",
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => (p.value ? String(p.value).substring(0, 10) : ""),
	},
	{
		field: "Rollset",
		headerName: "Roll Set",
		width: 120,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "RollBarrelDescription",
		headerName: "Roll Barrel Description",
		flex: 1,
		minWidth: 220,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Name",
		headerName: "Foundry Number",
		width: 140,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "RollMaterial",
		headerName: "Roll Material",
		width: 160,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Hardness",
		headerName: "Hardness",
		width: 100,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "CutNewDiameter",
		headerName: "New Diameter",
		width: 120,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => toFixedOrEmpty(p.value, 3),
	},
	{
		field: "Supplier",
		headerName: "Supplier",
		// The longest supplier, MILLER CENTRIFUGAL CASTING CO, on one line.
		width: 250,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Cost",
		headerName: "Cost",
		width: 110,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => toFixedOrEmpty(p.value, 3),
	},
];

const URL_SCHEMA = {
	costCenterId: urlParam.int(),
	start: urlParam.date(() => moment().startOf("day")),
	end: urlParam.date(() => moment().startOf("day")),
};

export const FirstTurnedRollsReport = () => {
	const [
		{ costCenterId, start, end },
		setFilters,
		{ dropValues, resetValues, isDefault },
	] = useUrlState(URL_SCHEMA);
	const [errorMsg, setErrorMsg] = useState("");
	const gridApiRef = useRef<GridApi<FirstTurnedRollRow> | null>(null);

	const startStr = start.format("YYYY-MM-DD");
	const endStr = end.format("YYYY-MM-DD");

	const costCentersQuery = useCostCenters();
	const costCenters = useMemo(
		() => costCentersQuery.data ?? [],
		[costCentersQuery.data],
	);

	// A shared link can name a cost center that is no longer active.
	useEffect(() => {
		if (!costCentersQuery.isSuccess || costCenterId == null) return;
		if (!costCenters.some((cc) => cc.Id === costCenterId))
			dropValues("costCenterId");
	}, [costCentersQuery.isSuccess, costCenters, costCenterId, dropValues]);

	// No auto-select: the page opens with no cost center chosen and an empty
	// grid, matching the legacy "Choose..." initial state. The data query stays
	// disabled (see useFirstTurnedRolls) until the user picks a cost center.
	const dataQuery = useFirstTurnedRolls({
		start: startStr,
		end: endStr,
		costCenterId,
	});

	const rows = dataQuery.data ?? [];

	const handleExportToExcel = useCallback(() => {
		if (!gridApiRef.current) return;
		gridApiRef.current.exportDataAsExcel({
			fileName: `First_Turned_Rolls_${startStr}_${endStr}.xlsx`,
			processCellCallback: (params) => {
				const colId = params.column.getColId();
				if (colId === "FirstTurnTimestampString") {
					return params.value ? String(params.value).substring(0, 10) : "";
				}
				if (colId === "CutNewDiameter" || colId === "Cost") {
					return toFixedOrEmpty(params.value, 3);
				}
				return params.value ?? "";
			},
		});
	}, [startStr, endStr]);

	const { exporting: exportingPdf, exportPdf } =
		useReportPdfExport(setErrorMsg);
	const costCenterName =
		costCenters.find((cc) => cc.Id === costCenterId)?.Name ?? "";
	const handleExportToPdf = () => {
		const api = gridApiRef.current;
		if (!api) return;
		const table = gridToPdfTable(api, {
			countLabel: "Total Rolls",
			sums: [{ colId: "Cost", label: "Total Cost" }],
			aligns: {
				RollBarrelDescription: "left",
				CutNewDiameter: "right",
				Cost: "right",
			},
		});
		exportPdf(
			`First Turned Rolls ${costCenterName} ${start.format("MM-DD-YYYY")} to ${end.format("MM-DD-YYYY")}.pdf`,
			() =>
				getReportPdf({
					title: "First Turned Rolls",
					filters: [
						{ label: "Start Date", value: start.format("MM/DD/YYYY") },
						{ label: "Close Date", value: end.format("MM/DD/YYYY") },
						{ label: "Cost Center", value: costCenterName },
					],
					tables: [table],
				}),
		);
	};

	const isInitialLoading =
		costCentersQuery.isLoading || (costCenterId != null && dataQuery.isLoading);

	if (dataQuery.error && !errorMsg) {
		setErrorMsg((dataQuery.error as Error).message);
	}
	if (costCentersQuery.error && !errorMsg) {
		setErrorMsg((costCentersQuery.error as Error).message);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				First Turned Rolls
			</Typography>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
				}}
			/>
			<Paper elevation={3}>
				<FirstTurnedRollsToolbar
					onReset={() => resetValues()}
					canReset={!isDefault()}
					start={start}
					end={end}
					onStartChange={(d) => isUsableDate(d) && setFilters({ start: d })}
					onEndChange={(d) => isUsableDate(d) && setFilters({ end: d })}
					costCenterId={costCenterId}
					onCostCenterChange={(id) => setFilters({ costCenterId: id })}
					costCenters={costCenters}
					onExportExcel={handleExportToExcel}
					onExportPdf={handleExportToPdf}
					canExportPdf={
						costCenterId != null && !isInitialLoading && rows.length > 0
					}
					exportingPdf={exportingPdf}
				/>
			</Paper>

			<FirstTurnedRollsSummary
				total={rows.length}
				money={{
					label: "Total Cost",
					value: rows.reduce((sum, row) => sum + (Number(row.Cost) || 0), 0),
				}}
			/>

			<Paper
				elevation={3}
				sx={{
					flex: 1,
					overflow: "hidden",
				}}
			>
				{isInitialLoading ? (
					<CenteredLazyLoading />
				) : (
					<AgGridReact<FirstTurnedRollRow>
						columnDefs={columnDefs}
						rowData={rows}
						rowModelType="clientSide"
						defaultColDef={{ resizable: true }}
						suppressContextMenu
						suppressCellFocus
						headerHeight={32}
						rowHeight={28}
						onGridReady={(e) => {
							gridApiRef.current = e.api;
						}}
					/>
				)}
			</Paper>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
